import React, { useState } from 'react';

interface UploadedFile {
  name: string;
  size: number;
  uploadedAt: string;
  uploaderName?: string;
  url?: string;
}

export default function ShareFiles() {
  const [mode, setMode] = useState<'choose' | 'send' | 'receive' | 'manage' | null>('choose');
  const [projectName, setProjectName] = useState('');
  const [slug, setSlug] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const generateSlug = (name: string) => {
    // Add random 6-character prefix to prevent collisions and improve security
    // Format: {random-id}/{readable-name}
    const randomId = Math.random().toString(36).substring(2, 8);
    const readableName = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${randomId}/${readableName}`;
  };

  const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setProjectName(name);
    setSlug(generateSlug(name));
  };

  const createLink = async () => {
    if (!projectName || !slug) return;

    setIsCreating(true);
    try {
      const response = await fetch('https://happyhumantools-d1-api.b-9f2.workers.dev/api/quick-upload-page', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: projectName,
          slug: slug
        })
      });

      const result = await response.json();
      if (result.success) {
        const link = `https://happyhumantools.com/files/${result.data.slug}`;
        setCreatedLink(link);

        // Store in localStorage
        const stored = JSON.parse(localStorage.getItem('myFileReceivers') || '[]');
        stored.push({
          id: result.data.id,
          slug: result.data.slug,
          name: projectName,
          url: link,
          createdAt: new Date().toISOString(),
          mode: mode
        });
        localStorage.setItem('myFileReceivers', JSON.stringify(stored));
      }
    } catch (error) {
      alert('Failed to create link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    let allUploadsSuccessful = true;

    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploaderName', 'Self');
      formData.append('uploaderEmail', 'self@upload.com');
      formData.append('businessSlug', slug);

      try {
        const response = await fetch('https://happyhumantools-d1-api.b-9f2.workers.dev/api/business-upload', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          console.error('Upload failed:', await response.text());
          allUploadsSuccessful = false;
        } else {
          const result = await response.json();
          console.log('Upload successful:', result);
        }
      } catch (error) {
        console.error('Upload failed:', error);
        allUploadsSuccessful = false;
      }
    }

    if (allUploadsSuccessful) {
      setUploadComplete(true);
    } else {
      alert('Some files failed to upload. Please try again.');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(createdLink);
    const btn = document.getElementById('copy-btn');
    if (btn) {
      btn.textContent = '✓ Copied!';
      setTimeout(() => { btn.textContent = 'Copy Link'; }, 2000);
    }
  };

  const shareViaEmail = (useGmail = false) => {
    const subject = encodeURIComponent(`Share files: ${projectName}`);
    const body = encodeURIComponent(`I've created a link for sharing files:\n\n${createdLink}\n\nClick the link to upload or download files.`);

    if (useGmail) {
      window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`);
    } else {
      window.open(`mailto:?subject=${subject}&body=${body}`);
    }
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Check out my shared files for ${projectName}: ${createdLink}`);
    window.open(`https://wa.me/?text=${text}`);
  };

  const shareViaSMS = () => {
    const text = encodeURIComponent(`Shared files: ${createdLink}`);
    window.open(`sms:?&body=${text}`);
  };

  const showQRCode = () => {
    const qrContainer = document.getElementById('qr-container');
    if (qrContainer) {
      qrContainer.innerHTML = `
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onclick="this.remove()">
          <div class="bg-white p-8 rounded-2xl" onclick="event.stopPropagation()">
            <h3 class="text-xl font-bold mb-4">QR Code</h3>
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(createdLink)}" alt="QR Code" />
            <p class="text-sm text-gray-600 mt-4 text-center">Click outside to close</p>
          </div>
        </div>
      `;
    }
  };

  // CHOOSE MODE
  if (mode === 'choose') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-2xl">
          <h1 className="text-6xl md:text-7xl font-bold mb-4 text-gray-900">
            Share Files
          </h1>
          <p className="text-xl text-gray-600 mb-12">
            The simplest way to share files with anyone
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-lg mx-auto">
            <button
              onClick={() => setMode('send')}
              className="group bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-200 hover:border-blue-500"
            >
              <div className="text-5xl mb-4">📤</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Send</h2>
              <p className="text-gray-600">Upload files and share them with others</p>
            </button>

            <button
              onClick={() => setMode('receive')}
              className="group bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-200 hover:border-purple-500"
            >
              <div className="text-5xl mb-4">📥</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Receive</h2>
              <p className="text-gray-600">Create a link for others to send you files</p>
            </button>
          </div>

          <div className="mt-12 text-sm text-gray-500">
            No signup required • Files stored securely • Works on all devices
          </div>
        </div>
      </div>
    );
  }

  // SEND MODE
  if (mode === 'send') {
    // Step 1: Name the project
    if (!createdLink) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
            <button onClick={() => setMode('choose')} className="mb-6 text-gray-500 hover:text-gray-700">
              ← Back
            </button>

            <div className="text-4xl mb-4">📤</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Send Files</h2>
            <p className="text-gray-600 mb-6">What are you sharing?</p>

            <input
              type="text"
              value={projectName}
              onChange={handleProjectNameChange}
              placeholder="e.g., Vacation Photos, Project Files, Documents"
              className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none mb-3"
              autoFocus
            />

            {projectName && (
              <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Your link will be:</p>
                <p className="font-mono text-sm text-gray-700">
                  happyhumantools.com/files/<span className="font-bold">{slug}</span>
                </p>
              </div>
            )}

            <button
              onClick={createLink}
              disabled={!projectName || isCreating}
              className={`w-full py-4 text-lg font-medium rounded-xl transition-all ${
                projectName && !isCreating
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400'
              }`}
            >
              {isCreating ? 'Creating...' : 'Continue'}
            </button>
          </div>
        </div>
      );
    }

    // Step 2: Upload files
    if (!uploadComplete) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Upload Your Files</h2>

            <div
              className="border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <div className="text-6xl mb-4">📁</div>
              <p className="text-xl text-gray-700 mb-2">Click to select files</p>
              <p className="text-gray-500">or drag and drop them here</p>
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Selected Files ({selectedFiles.length})</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedFiles.map((file, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span>📄</span>
                      <span className="truncate">{file.name}</span>
                      <span className="text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={uploadFiles}
                  className="w-full mt-6 py-4 bg-green-600 text-white text-lg font-medium rounded-xl hover:bg-green-700 transition-colors"
                >
                  Upload & Continue
                </button>
              </div>
            )}

            <button
              onClick={() => setUploadComplete(true)}
              className="w-full mt-4 py-3 text-gray-600 hover:text-gray-800"
            >
              Skip upload for now →
            </button>
          </div>
        </div>
      );
    }

    // Step 3: Share options
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Ready to Share!</h2>
            <p className="text-gray-600">Your files are uploaded. Now share the link:</p>
          </div>

          {/* Prominent Share Button for Mobile */}
          <button
            onClick={copyLink}
            className="w-full mb-4 py-4 bg-blue-600 text-white text-lg font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center justify-center gap-2"
          >
            <span className="text-xl">📋</span>
            <span>Tap to Copy Share Link</span>
          </button>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex gap-2">
              <input
                type="text"
                value={createdLink}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
              />
              <button
                id="copy-btn"
                onClick={copyLink}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Copy Link
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="relative group">
              <button
                onClick={() => shareViaEmail(false)}
                className="w-full flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 transition-colors">
                <span className="text-2xl">📧</span>
                <span>Email Link</span>
              </button>
              <button
                onClick={() => shareViaEmail(true)}
                className="absolute top-1 right-1 px-2 py-1 text-xs bg-white border border-gray-200 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-gray-50">
                Gmail
              </button>
            </div>
            <button
              onClick={shareViaWhatsApp}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 transition-colors">
              <span className="text-2xl">💬</span>
              <span>WhatsApp</span>
            </button>
            <button
              onClick={shareViaSMS}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-400 transition-colors">
              <span className="text-2xl">📱</span>
              <span>Text Message</span>
            </button>
            <button
              onClick={showQRCode}
              className="flex items-center justify-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 transition-colors">
              <span className="text-2xl">📲</span>
              <span>QR Code</span>
            </button>
          </div>
          <div id="qr-container"></div>

          <div className="flex gap-4">
            <a
              href={createdLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center py-3 bg-green-600 text-white rounded-xl hover:bg-green-700"
            >
              View Upload Page
            </a>
            <button
              onClick={() => { setMode('manage'); loadUploadedFiles(); }}
              className="flex-1 text-center py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
            >
              View Uploaded Files
            </button>
          </div>
        </div>
      </div>
    );
  }

  // RECEIVE MODE
  if (mode === 'receive') {
    if (!createdLink) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
            <button onClick={() => setMode('choose')} className="mb-6 text-gray-500 hover:text-gray-700">
              ← Back
            </button>

            <div className="text-4xl mb-4">📥</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Receive Files</h2>
            <p className="text-gray-600 mb-6">What files are you collecting?</p>

            <input
              type="text"
              value={projectName}
              onChange={handleProjectNameChange}
              placeholder="e.g., Wedding Photos, Event Registration, Homework"
              className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none mb-3"
              autoFocus
            />

            {projectName && (
              <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Your link will be:</p>
                <p className="font-mono text-sm text-gray-700">
                  happyhumantools.com/files/<span className="font-bold">{slug}</span>
                </p>
              </div>
            )}

            <button
              onClick={createLink}
              disabled={!projectName || isCreating}
              className={`w-full py-4 text-lg font-medium rounded-xl transition-all ${
                projectName && !isCreating
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-400'
              }`}
            >
              {isCreating ? 'Creating...' : 'Create Upload Link'}
            </button>
          </div>
        </div>
      );
    }

    // Share the receive link
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">✨</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Your Upload Link is Ready!</h2>
            <p className="text-gray-600">Share this link to start receiving files</p>
          </div>

          {/* Prominent Share Button for Mobile */}
          <button
            onClick={copyLink}
            className="w-full mb-4 py-4 bg-purple-600 text-white text-lg font-semibold rounded-xl hover:bg-purple-700 transition-all shadow-md flex items-center justify-center gap-2"
          >
            <span className="text-xl">📋</span>
            <span>Tap to Copy Share Link</span>
          </button>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex gap-2">
              <input
                type="text"
                value={createdLink}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
              />
              <button
                id="copy-btn"
                onClick={copyLink}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Copy Link
              </button>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">📮 How it works:</h3>
            <ol className="space-y-2 text-gray-700">
              <li>1. Share this link with anyone who needs to send you files</li>
              <li>2. They can upload files without creating an account</li>
              <li>3. Files appear in your dashboard instantly</li>
              <li>4. Download files individually or all at once</li>
            </ol>
          </div>

          <div className="flex gap-4">
            <a
              href="/dashboard"
              className="flex-1 text-center py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
            >
              View Dashboard
            </a>
            <button
              onClick={() => { setMode('choose'); setCreatedLink(''); setProjectName(''); }}
              className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
            >
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for file management
  const loadUploadedFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await fetch(`https://happyhumantools-d1-api.b-9f2.workers.dev/api/list-files?businessSlug=${slug}`);
      const data = await response.json();
      if (data.success) {
        setUploadedFiles(data.files || []);
      }
    } catch (error) {
      console.error('Failed to load files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // MANAGE MODE - View uploaded files
  if (mode === 'manage') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => setMode('choose')} className="mb-6 text-gray-500 hover:text-gray-700">
            ← Back to Share Options
          </button>

          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">{projectName}</h2>
              <p className="text-gray-600">{uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded</p>
            </div>

            {/* Share Link Section */}
            <div className="mb-6 p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-gray-600 mb-2">Share this link to receive more files:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={createdLink}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                />
                <button
                  onClick={copyLink}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Copy Link
                </button>
              </div>
            </div>

            {loadingFiles ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : uploadedFiles.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-xl text-gray-600">No files uploaded yet</p>
                <p className="text-gray-500 mt-2">Share your upload link to start receiving files</p>
                <div className="mt-6 p-4 bg-gray-50 rounded-xl inline-block">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={createdLink}
                      readOnly
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                    />
                    <button
                      onClick={copyLink}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📄</span>
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {formatDate(file.uploadedAt)}
                          {file.uploaderName && ` • From ${file.uploaderName}`}
                        </p>
                      </div>
                    </div>
                    {file.url && (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex gap-3">
                <button
                  onClick={loadUploadedFiles}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  🔄 Refresh
                </button>
                <button
                  onClick={() => setUploadComplete(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                >
                  Upload More Files
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}